import { createStore } from "solid-js/store";
import { render, For } from 'solid-js/web'
import { onMount, createMemo, createResource } from 'solid-js'
import styles from './toast.module.css'
import { uuidv4 } from "./util";

export type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type ToastType = 'success' | 'notice' | 'error';

// The full Synergy shell provides jQuery + the pnotify plugin globally, so shell-hosted
// pages route toasts through it. Standalone STApp pages (served straight from pages/st/*.html)
// have no jQuery, so we fall back to the built-in SolidJS toaster. This also covers DEV,
// where the bundle never loads jQuery.
const hasPnotify = (): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jq = (globalThis as any).$;
    return typeof jq === 'function' && typeof jq.pnotify_success === 'function';
}

// `icon` is a CSS icon-font class (e.g. "fa fa-user"), forwarded as-is into PNotify's own `icon`
// option (see Revelation/RT/js/ST/STX_pnotify-ext.js) - the same mechanism PNotify uses to render
// its own default success/notice/error icons, just with a caller-supplied class instead.
export const notify = (type: ToastType, message: string, icon?: string) => {
    if (import.meta.env.DEV || !hasPnotify()) {
        ensureToaster('top-right');
        setToasts(toasts.length, { id: uuidv4(), type, message, duration: 5000, icon });
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jq = (globalThis as any).$;
    const args = icon ? { icon } : undefined;

    switch(type) {
        case 'success':
            jq.pnotify_success(message, args)
            break;
        case 'notice':
            jq.pnotify_notice(message, args)
            break;
        case 'error':
            jq.pnotify_error(message, args)
            break;
    }
}

type ToastProps = {
    id: string;
    type: ToastType;
    message: string;
    duration?: number; // in milliseconds
    onClose?: () => void; // callback when the toast is closed
    onClick?: () => void; // callback when the toast is clicked
    position?: ToastPosition;
    icon?: string; // overrides the type's default icon class (DEV preview only, not styled like production's fa-solid convention)
}

type ToastInfo = ToastProps & {
    leaving?: boolean; // used for animations
}

const [toasts, setToasts] = createStore<ToastInfo[]>([])

const ensureToaster = (position: ToastPosition) => {
    
    const existing = document.querySelector(`.${styles['toaster']}[data-position="${position}"]`);
    if (existing)
        return;

    render(() => <Toaster position={position} />, document.body);
}

function Toaster(props: { position: ToastPosition }) {
    return (
        <div class={styles['toaster']} data-position={props.position}>
            <For each={toasts}>{(toast) => (
                <Toast {...toast} />
            )}</For>
        </div>
    )
}

function Toast(props: ToastInfo) {
    onMount(() => {
        setTimeout(() => {
            dismiss();
        }, props.duration || 5000);
    })

    const dismiss = () => {
        setToasts(t => t.id === props.id, 'leaving', true);
        setTimeout(() => {
            setToasts(toasts.filter(t => t.id !== props.id));
            props.onClose?.();
        }, 300)
    }

    const title = createMemo(() => {
        switch (props.type) {
            case 'success': return 'Success';
            case 'notice': return 'Notice';
            case 'error': return 'Error';
            default: return 'Notification';
        }
    })
    
    const icon = createMemo(() => {
        switch (props.type) {
            case 'success': return 'fa-check-circle';
            case 'notice': return 'fa-info-circle';
            case 'error': return 'fa-exclamation-triangle';
            default: return 'fa-bell';
        }
    })

    return (
        <div
            class={styles['toast']}
            onClick={() => props.onClick?.()}
            data-position={props.position || 'top-right'}
            data-type={props.type}
            data-leaving={props.leaving ? 'true' : undefined}
        >
            <div class={styles['toast-header']}>
                <div class={styles['toast-title']}>
                    <i class={props.icon || `fa-solid ${icon()}`} />
                    {title()}
                </div>
                <button class={styles['toast-close']} onClick={() => dismiss()}>
                    <i class='fa-solid fa-xmark' />
                </button>
            </div>

            <div class={styles['toast-content']}>
                <p>{props.message}</p>
            </div>

            <div class={styles['toast-actions']}>

            </div>
        </div>
    )
}